import { api } from './api';

export type ConferencePurpose =
  | 'screening'
  | 'case_prep'
  | 'court_day'
  | 'settlement'
  | 'post_trial'
  | 'other';

export type ConferenceFormat = 'in_chambers' | 'at_court' | 'video' | 'phone';

// Coordination state machine — clerk coordinates, barrister decides, solicitor informed.
export type ConferenceStatus =
  | 'requested'
  | 'awaiting_barrister'
  | 'barrister_approved'
  | 'barrister_declined'
  | 'alternative_suggested'
  | 'confirmed'
  | 'completed'
  | 'cancelled';

export interface Conference {
  id: string;
  chambersId: string;
  caseId: string;
  status: ConferenceStatus;
  purpose: ConferencePurpose;
  layClientAttending: boolean;
  format: ConferenceFormat;
  scheduledAt: string | null;
  durationMinutes: number;
  proposedDates: string[] | null;
  proposedById: string | null;
  barristerChosenDate: string | null;
  barristerSuggestedDates: string[] | null;
  barristerResponseNote: string | null;
  outcome: string | null;
  locationDetails: string | null;
  requestedById: string;
  scheduledById: string | null;
  cancelledById: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  calendarEventId: string | null;
  clerkCalendarEventId: string | null;
  notes: string | null;
  completedAt: string | null;
  completedById: string | null;
  createdAt: string;
  updatedAt: string;
  case: { id: string; caseNumber: string; caseName: string; leadBarrister?: { userId: string | null } | null };
  requestedBy: { id: string; firstName: string; lastName: string; memberType: string };
  scheduledBy: { id: string; firstName: string; lastName: string } | null;
  cancelledBy: { id: string; firstName: string; lastName: string } | null;
  triggeringAllocation: {
    id: string;
    status: string;
    barrister: {
      id: string;
      user: { id: string; firstName: string; lastName: string };
    };
  } | null;
}

export type ConferenceStatusFilter = 'awaiting' | 'scheduled' | 'cancelled' | 'completed';

export interface ListConferencesQuery {
  caseId?: string;
  status?: ConferenceStatusFilter;
  page?: number;
  limit?: number;
}

export interface ListConferencesResponse {
  conferences: Conference[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export async function fetchConferences(query: ListConferencesQuery = {}): Promise<ListConferencesResponse> {
  const res = await api.get<ListConferencesResponse>('/conferences', { params: query });
  return res.data;
}

export async function fetchConference(id: string): Promise<Conference> {
  const res = await api.get<{ conference: Conference }>(`/conferences/${id}`);
  return res.data.conference;
}

export interface ConfirmSlotPayload {
  selectedAt: string; // ISO 8601 datetime
  durationMinutes?: number;
  location?: string;
  videoUrl?: string;
  notes?: string;
}

export interface ConfirmSlotResult {
  conference: Conference;
  calendarEvent: {
    id: string;
    startAt: string;
    endAt: string;
    title: string;
  };
  wasProposedSlot: boolean;
}

export async function confirmConferenceSlot(id: string, payload: ConfirmSlotPayload): Promise<ConfirmSlotResult> {
  const res = await api.post<ConfirmSlotResult>(`/conferences/${id}/confirm-slot`, payload);
  return res.data;
}

export async function cancelConference(id: string, reason: string): Promise<Conference> {
  const res = await api.post<{ conference: Conference }>(`/conferences/${id}/cancel`, { reason });
  return res.data.conference;
}

export async function addProposedDates(id: string, proposedDates: string[]): Promise<Conference> {
  const res = await api.post<{ conference: Conference }>(`/conferences/${id}/proposed-dates`, { proposedDates });
  return res.data.conference;
}

export interface CreateConferenceRequestPayload {
  caseId: string;
  purpose: ConferencePurpose;
  format: ConferenceFormat;
  durationMinutes?: number;
  layClientAttending?: boolean;
  notes?: string;
}

/** Clerk opens a Conference Request (e.g. a solicitor phoned needing counsel). */
export async function createConferenceRequest(payload: CreateConferenceRequestPayload): Promise<Conference> {
  const res = await api.post<{ conference: Conference }>('/conferences', payload);
  return res.data.conference;
}

// ─── Ownership handshake: clerk proposes → barrister decides → clerk finalises ──

export interface ProposeSlotsPayload {
  proposedDates: string[]; // ISO datetimes
  durationMinutes?: number;
  locationDetails?: string;
}

/** Clerk proposes candidate slots → status awaiting_barrister; barrister notified. */
export async function proposeConferenceSlots(id: string, payload: ProposeSlotsPayload): Promise<Conference> {
  const res = await api.post<{ conference: Conference }>(`/conferences/${id}/propose-slots`, payload);
  return res.data.conference;
}

export interface RespondProposalPayload {
  decision: 'approve' | 'decline' | 'suggest';
  chosenDate?: string;       // required for approve
  suggestedDates?: string[]; // required for suggest
  note?: string;
}

/** Barrister approves a slot / declines / suggests alternatives. Never schedules. */
export async function respondToConferenceProposal(id: string, payload: RespondProposalPayload): Promise<Conference> {
  const res = await api.post<{ conference: Conference }>(`/conferences/${id}/respond`, payload);
  return res.data.conference;
}

export interface CompleteConferenceResult {
  conference: Conference;
  feeRecord: {
    id: string;
    feeType: string;
    description: string;
    durationMinutes: number | null;
    amount: string;
    isBillable: boolean;
    caseId: string;
  };
}

export async function completeConference(id: string, outcome?: string): Promise<CompleteConferenceResult> {
  const res = await api.post<CompleteConferenceResult>(`/conferences/${id}/complete`, outcome ? { outcome } : {});
  return res.data;
}

export const CONFERENCE_STATUS_LABELS: Record<ConferenceStatus, string> = {
  requested: 'Awaiting proposal',
  awaiting_barrister: 'Awaiting barrister decision',
  barrister_approved: 'Awaiting solicitor confirmation',
  barrister_declined: 'Needs rescheduling',
  alternative_suggested: 'Needs rescheduling',
  confirmed: 'Confirmed',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export const PURPOSE_LABELS: Record<ConferencePurpose, string> = {
  screening: 'Screening',
  case_prep: 'Case prep',
  court_day: 'Court day',
  settlement: 'Settlement',
  post_trial: 'Post-trial',
  other: 'Other',
};

export const FORMAT_LABELS: Record<ConferenceFormat, string> = {
  in_chambers: 'In chambers',
  at_court: 'At court',
  video: 'Video',
  phone: 'Phone',
};
